import React, { useEffect, useState } from "react";
import { Button, Spinner, Badge, Form, Modal, Tabs, Tab, Alert } from "react-bootstrap";
import { 
  getPendingEmails, 
  getRejectedEmails, 
  getApprovalHistory, 
  approveEmails, 
  rejectEmails,
  PendingEmail 
} from "@/api/adminApi";
import { FaCheckCircle, FaTimesCircle, FaEye, FaClock, FaEnvelope } from "react-icons/fa";
import "./AdminEmailApproval.scss";

const AdminEmailApproval: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>("pending");
  const [pendingEmails, setPendingEmails] = useState<PendingEmail[]>([]);
  const [rejectedEmails, setRejectedEmails] = useState<PendingEmail[]>([]);
  const [historyEmails, setHistoryEmails] = useState<PendingEmail[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [previewEmail, setPreviewEmail] = useState<PendingEmail | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadEmails();
  }, [activeTab]);

  const loadEmails = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === "pending") {
        const data = await getPendingEmails();
        setPendingEmails(data);
      } else if (activeTab === "rejected") {
        const data = await getRejectedEmails();
        setRejectedEmails(data);
      } else if (activeTab === "history") {
        const data = await getApprovalHistory();
        setHistoryEmails(data);
      }
    } catch (err: any) {
      console.error("Failed to load emails:", err);
      setError(err.response?.data?.message || "Failed to load emails");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEmail = (guid: string) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(guid)) {
      newSelected.delete(guid);
    } else {
      newSelected.add(guid);
    }
    setSelectedEmails(newSelected);
  };

  const handleSelectAll = (emails: PendingEmail[]) => {
    if (selectedEmails.size === emails.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(emails.map(e => e.guid)));
    }
  };

  const handleApprove = async () => {
    if (selectedEmails.size === 0) return;
    
    if (!confirm(`Are you sure you want to approve ${selectedEmails.size} email(s)?`)) {
      return;
    }

    try {
      const result = await approveEmails(Array.from(selectedEmails));
      setSuccess(`Approved ${result.approvedCount} email(s)`);
      setSelectedEmails(new Set());
      await loadEmails();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to approve emails");
    }
  };

  const handleReject = async () => {
    if (selectedEmails.size === 0) return;
    
    if (!confirm(`Are you sure you want to reject ${selectedEmails.size} email(s)?`)) {
      return;
    }

    try {
      const result = await rejectEmails(Array.from(selectedEmails));
      setSuccess(`Rejected ${result.rejectedCount} email(s)`);
      setSelectedEmails(new Set());
      await loadEmails();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to reject emails");
    }
  };

  const handlePreview = (email: PendingEmail) => {
    setPreviewEmail(email);
    setShowPreview(true);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getEmailAge = (createDt: string) => {
    const created = new Date(createDt);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays > 7) {
      return { warning: true, text: `${diffDays} days old` };
    }
    return { warning: false, text: `${diffDays} days old` };
  };

  const renderEmailTable = (emails: PendingEmail[], showActions: boolean = true) => {
    return (
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              {showActions && (
                <th style={{ width: "50px" }}>
                  <Form.Check
                    type="checkbox"
                    checked={selectedEmails.size === emails.length && emails.length > 0}
                    onChange={() => handleSelectAll(emails)}
                  />
                </th>
              )}
              <th>Recipient</th>
              <th>Subject</th>
              <th>Created</th>
              <th>Scheduled</th>
              <th>Creator</th>
              {activeTab === "history" && <th>Approved By</th>}
              {activeTab === "history" && <th>Approved Date</th>}
              <th style={{ width: "100px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {emails.map((email) => {
              const age = getEmailAge(email.createDt);
              return (
                <tr key={email.guid}>
                  {showActions && (
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedEmails.has(email.guid)}
                        onChange={() => handleSelectEmail(email.guid)}
                      />
                    </td>
                  )}
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <FaEnvelope className="text-muted" />
                      <span>{email.recipientEmail}</span>
                    </div>
                  </td>
                  <td>
                    <div>
                      {email.emailSubject}
                      {email.addAttachment && (
                        <Badge bg="info" className="ms-2">Attachment</Badge>
                      )}
                    </div>
                  </td>
                  <td>
                    <div>
                      {formatDate(email.createDt)}
                      {age.warning && (
                        <div>
                          <Badge bg="warning" className="mt-1">
                            <FaClock className="me-1" />
                            {age.text}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{formatDate(email.scheduledSendDt)}</td>
                  <td>{email.createUser}</td>
                  {activeTab === "history" && <td>{email.autoSendApprovedBy || "-"}</td>}
                  {activeTab === "history" && <td>{email.autoSendApprovedDt ? formatDate(email.autoSendApprovedDt) : "-"}</td>}
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handlePreview(email)}
                    >
                      <FaEye /> Preview
                    </Button>
                  </td>
                </tr>
              );
            })}
            {emails.length === 0 && (
              <tr>
                <td colSpan={showActions ? 8 : 7} className="text-center text-muted py-4">
                  No emails found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="admin-email-approval container-fluid p-4">
      <div className="mb-4">
        <h4 className="mb-0">Email Approval Queue</h4>
        <p className="text-muted">Review and approve system-generated emails</p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {activeTab === "pending" && selectedEmails.size > 0 && (
        <div className="mb-3 d-flex gap-2">
          <Button 
            variant="success" 
            onClick={handleApprove}
            disabled={selectedEmails.size === 0}
          >
            <FaCheckCircle className="me-2" />
            Approve Selected ({selectedEmails.size})
          </Button>
          <Button 
            variant="danger" 
            onClick={handleReject}
            disabled={selectedEmails.size === 0}
          >
            <FaTimesCircle className="me-2" />
            Reject Selected ({selectedEmails.size})
          </Button>
        </div>
      )}

      <Tabs activeKey={activeTab} onSelect={(k) => {
        setActiveTab(k || "pending");
        setSelectedEmails(new Set());
      }} className="mb-3">
        <Tab eventKey="pending" title={`Pending (${pendingEmails.length})`}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            renderEmailTable(pendingEmails, true)
          )}
        </Tab>
        <Tab eventKey="rejected" title={`Rejected (${rejectedEmails.length})`}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            renderEmailTable(rejectedEmails, false)
          )}
        </Tab>
        <Tab eventKey="history" title="Approval History">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            renderEmailTable(historyEmails, false)
          )}
        </Tab>
      </Tabs>

      {/* Email Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Email Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewEmail && (
            <div>
              <div className="mb-3">
                <strong>From:</strong> {previewEmail.senderEmail}
              </div>
              <div className="mb-3">
                <strong>To:</strong> {previewEmail.recipientEmail}
              </div>
              {previewEmail.copyEmail && previewEmail.copyEmail !== '[]' && (
                <div className="mb-3">
                  <strong>CC:</strong> {previewEmail.copyEmail}
                </div>
              )}
              {previewEmail.bccEmail && previewEmail.bccEmail !== '[]' && (
                <div className="mb-3">
                  <strong>BCC:</strong> {previewEmail.bccEmail}
                </div>
              )}
              <div className="mb-3">
                <strong>Subject:</strong> {previewEmail.emailSubject}
              </div>
              {previewEmail.addAttachment && previewEmail.attachmentPath && (
                <div className="mb-3">
                  <strong>Attachments:</strong> {previewEmail.attachmentPath}
                </div>
              )}
              <div className="mb-3">
                <strong>Scheduled:</strong> {formatDate(previewEmail.scheduledSendDt)}
              </div>
              <hr />
              <div className="mb-3">
                <strong>Message:</strong>
                <div 
                  className="mt-2 p-3 border rounded email-preview-body"
                  dangerouslySetInnerHTML={{ __html: previewEmail.emailMessage || "No content" }}
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="warning" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminEmailApproval;
